import React, { createContext, useContext, useRef, useCallback } from 'react';

const SystemWebSocketContext = createContext(null);

export const useSystemWebSocket = () => {
  const context = useContext(SystemWebSocketContext);
  if (!context) {
    throw new Error('useSystemWebSocket must be used within SystemWebSocketProvider');
  }
  return context;
};

export const SystemWebSocketProvider = ({ children }) => {
  const messageHandlersRef = useRef(new Map());
  const socketRef = useRef(null);

  const subscribe = useCallback((messageType, handler) => {
    const handlers = messageHandlersRef.current.get(messageType) || [];
    handlers.push(handler);
    messageHandlersRef.current.set(messageType, handlers);

    // Return unsubscribe function
    return () => {
      const currentHandlers = messageHandlersRef.current.get(messageType) || [];
      const filtered = currentHandlers.filter(h => h !== handler);
      if (filtered.length === 0) {
        messageHandlersRef.current.delete(messageType);
      } else {
        messageHandlersRef.current.set(messageType, filtered);
      }
    };
  }, []);

  const notifyHandlers = useCallback((messageType, data) => {
    const handlers = messageHandlersRef.current.get(messageType) || [];
    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in message handler for ${messageType}:`, error);
      }
    });
  }, []);

  const send = useCallback((message) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      try {
        socketRef.current.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error('[SystemWebSocket] Error sending message:', error);
        return false;
      }
    }
    return false;
  }, []);

  const setSocket = useCallback((socket) => {
    socketRef.current = socket;
  }, []);

  const isConnected = useCallback(() => {
    return socketRef.current && socketRef.current.readyState === WebSocket.OPEN;
  }, []);

  return (
    <SystemWebSocketContext.Provider value={{ subscribe, notifyHandlers, send, setSocket, isConnected }}>
      {children}
    </SystemWebSocketContext.Provider>
  );
};

